import {css} from '@emotion/core';

const IS_RESPONSIVE = true;
const FONT_BASE = 16;

export const BP_XXS_MIN = 359;
export const BP_XS_MIN = 480;
export const BP_S_MIN = 640;
export const BP_M_MIN = 768;
export const BP_L_MIN = 1024;
export const BP_XL_MIN = 1244;
export const BP_XXL_MIN = 1410;

export const BP_XXS_MAX = BP_XS_MIN - 1;
export const BP_XS_MAX = BP_S_MIN - 1;
export const BP_S_MAX = BP_M_MIN - 1;
export const BP_M_MAX = BP_L_MIN - 1;
export const BP_L_MAX = BP_XL_MIN - 1;
export const BP_XL_MAX = BP_XXL_MIN - 1;

const BP_LIST_MIN = {
	xxs: BP_XXS_MIN,
	xs: BP_XS_MIN,
	s: BP_S_MIN,
	m: BP_M_MIN,
	l: BP_L_MIN,
	xl: BP_XL_MIN,
	xxl: BP_XXL_MIN,
};

const BP_LIST_MAX = {
	xxs: BP_XXS_MAX,
	xs: BP_XS_MAX,
	s: BP_S_MAX,
	m: BP_M_MAX,
	l: BP_L_MAX,
	xl: BP_XL_MAX,
};

const BP_STATIC_MIN = 0;
const BP_STATIC_MAX = BP_XL_MAX;

const BP_PRINT_MIN = 0;
const BP_PRINT_MAX = 550;

/**
 * Transforms a number to an em unit
 * @param {number} number Number to be transformed to an em unit
 * @returns {string} number as *em* units
 */
export function bpAddEm(number) {
	if (typeof number !== 'number') {
		throw new TypeError(`Expected number but was passed ${typeof number}: ${number}`);
	}
	return `${number}em`;
}

/**
 * Transforms pixels (numeric) to em (numeric)
 * @param {number} pxPoint Amount of pixels to be transformed to em units
 * @returns {number} Equivalent *em* units
 */
export function bpPxToEms(pxPoint) {
	return pxPoint / FONT_BASE;
}

/**
 * Converts number of pixels or screen constants to equivalent em units
 * @param {number|string} point Can be in amount of pixels or screen constants (xxl, xxs, m, etc)
 * @param {boolean} isMax if the max list is used
 * @returns {number|string} Equivalent em units of pixels or screen constant
 */
export function bpToEms(point, isMax = false) {
	if (typeof point === 'number') {
		return bpPxToEms(point);
	}

	const list = isMax ? BP_LIST_MAX : BP_LIST_MIN;
	return list[point] ? bpPxToEms(list[point]) : '1em';
}

/**
 * Checks if the min and max is valid
 * @param {number} validMin Valid min of property
 * @param {number} validMax Valid max of property
 * @param {number|string} min Min in pixels or as a screen constant
 * @param {number|string} max Max in pixels pr as a screen constant
 * @param {string} property The property to checked (defaults to `'width'`)
 */
export function bpIsValidBetween(validMin, validMax, min, max = 0, property = 'width') {
	if (property !== 'width') {
		return false;
	}

	const validMinEms = bpToEms(validMin);
	const validMaxEms = bpToEms(validMax);
	const minEms = bpToEms(min);
	const maxEms = bpToEms(max);

	if (max === 0) {
		return minEms >= validMinEms && minEms < validMaxEms;
	}

	if (min === 0) {
		return maxEms >= validMaxEms;
	}

	if (minEms > validMaxEms || maxEms < validMinEms) {
		return false;
	}

	if (maxEms < validMaxEms) {
		return false;
	}

	return true;
}

/**
 * Checks if the content should be in the print stylesheet
 * @param {number|string} min Min in pixels or as a screen constant
 * @param {number|string} max Max in pixels or as a screen constant
 * @param {string} property The property to checked (defaults to `'width'`)
 */
export function bpIsPrint(min, max = 0, property = 'width') {
	return bpIsValidBetween(BP_PRINT_MIN, BP_PRINT_MAX, min, max, property);
}

/**
 * Checks if the content should be in the static stylesheet
 * @param {number|string} min Min in pixels or as a screen constant
 * @param {number|string} max Max in pixels or as a screen constant
 * @param {string} property The property to checked (defaults to `'width'`)
 */
export function bpIsStatic(min, max = 0, property = 'width') {
	return bpIsValidBetween(BP_STATIC_MIN, BP_STATIC_MAX, min, max, property);
}

/**
 * Returns a min-width media query based on bp name or px
 * @param {import('@emotion/core').SerializedStyles} content The style block
 * @param {number|string} min Min in pixels or as a screen constant
 * @param {string} property The property to be calculated
 */
export function bpMin(content, min, property = 'width') {
	if (IS_RESPONSIVE) {
		const _bpMin = bpAddEm(bpToEms(min, false));
		const bpType = `min-${property}`;

		if (bpIsPrint(min, 0, property)) {
			return css`
				@media (${bpType}: ${_bpMin}) { ${content} }
			`;
		} else {
			return css`
				@media only screen and (${bpType}: ${_bpMin}) { ${content} }
			`;
		}
	} else {
		if (bpIsStatic(min, 0, property)) {
			if (bpIsPrint(min, 0, property)) {
				return content;
			} else {
				return css`
					@media screen {
						${content}
					}
				`;
			}
		}
	}
}

/**
 * Returns a max-width media query based on bp name or px
 * @param {import('@emotion/core').SerializedStyles} content The style block
 * @param {number|string} max Max in pixels or as a screen constant
 * @param {string} property The property to be calculated
 */
export function bpMax(content, max, property = 'width') {
	if (IS_RESPONSIVE) {
		const _bpMax = bpAddEm(bpToEms(max, true));
		const bpType = `max-${property}`;

		if (bpIsPrint(0, max, property)) {
			return css`
				@media (${bpType}: ${_bpMax}) { ${content} }
			`;
		} else {
			return css`
				@media only screen and (${bpType}: ${_bpMax}) { ${content} }
			`;
		}
	} else {
		if (bpIsStatic(0, max, property)) {
			if (bpIsPrint(0, max, property)) {
				return content;
			} else {
				return css`
					@media screen {
						${content}
					}
				`;
			}
		}
	}
}

/**
 * Returns a min-width and max-width media query based on bp name (can be the same bp name) or px
 * @param {import('@emotion/core').SerializedStyles} content The style block
 * @param {number|string} min Min in pixels or as a screen constant
 * @param {number|string} max Max in pixels or as a screen constant
 * @param {string} property The property to be calculated
 */
export function bpBetween(content, min, max, property = 'width') {
	if (IS_RESPONSIVE) {
		const _bpMin = bpAddEm(bpToEms(min, false));
		const _bpMax = bpAddEm(bpToEms(max, true));

		const bpTypeMin = `min-${property}`;
		const bpTypeMax = `max-${property}`;

		if (bpIsPrint(min, max, property)) {
			return css`
				@media (${bpTypeMin}: ${_bpMin}) and (${bpTypeMax}: ${_bpMax}) { ${content} }
			`;
		} else {
			return css`
				@media only screen and (${bpTypeMin}: ${_bpMin}) and (${bpTypeMax}: ${_bpMax}) { ${content} }
			`;
		}
	} else {
		if (bpIsStatic(min, max, property)) {
			if (bpIsPrint(min, max, property)) {
				return content;
			} else {
				return css`
					@media screen {
						${content}
					}
				`;
			}
		}
	}
}

/**
 * Returns a min-width and max-width media query based on bp name (can be the same bp name) or px
 * @param {import('@emotion/core').SerializedStyles} content The style block
 * @param {number|string} min Min in pixels or as a screen constant
 * @param {number|string} max Max in pixels or as a screen constant
 * @param {string} property The property to be calculated
 */
export function bp(content, min, max = 0, property = 'width') {
	if (min === 'retina' || min === 'hdpi') {
		return css`
			@media (-webkit-min-device-pixel-ratio: 1.5),
				(min--moz-device-pixel-ratio: 1.5),
				(-o-min-device-pixel-ratio: 3/2),
				(min-device-pixel-ratio: 1.5) {
				${content};
			}
		`;
	} else if (min === 'static') {
		if (!IS_RESPONSIVE) {
			return content;
		}
	} else if (max === 0) {
		return bpMin(content, min, property);
	} else if (min === 0) {
		return bpMax(content, max, property);
	} else {
		return bpBetween(content, min, max, property);
	}
}

export default bp;
